<template>
  <section id="portfolio" class="section bg-white dark:bg-dark-bg">
    <div class="container-custom">
      <!-- Section Header -->
      <div class="text-center mb-12 animate-reveal">
        <h2 class="text-4xl md:text-5xl font-display font-bold text-gray-900 dark:text-white mb-4">
          Case Studies
        </h2>
        <p class="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          Real results for real clients
        </p>
      </div>

      <!-- Filter Tabs -->
      <div class="flex justify-center gap-3 mb-12 flex-wrap">
        <button
          v-for="filter in filters"
          :key="filter"
          @click="changeFilter(filter)"
          :class="[
            'px-6 py-2 rounded-full font-semibold text-sm transition-all duration-300',
            activeFilter === filter
              ? 'bg-accent text-white shadow-lg shadow-accent/30'
              : 'bg-gray-100 dark:bg-dark-surface text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
          ]"
        >
          {{ filter }}
        </button>
      </div>

      <!-- All Projects -->
      <div v-if="filteredProjects.length">
        <h3 class="text-2xl md:text-3xl font-display font-bold text-gray-900 dark:text-white mb-8">
          {{ activeFilter === 'All' ? 'All Projects' : `${activeFilter} Projects` }}
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <CaseStudyCard
            v-for="project in visibleProjects"
            :key="project.id"
            :project="project"
            class="portfolio-item"
            @open="openModal"
          />
        </div>

        <!-- Load More Button -->
        <div v-if="visibleProjects.length < filteredProjects.length" class="flex justify-center mt-12">
          <button
            @click="loadMore"
            class="px-8 py-3 bg-accent text-white font-semibold rounded-full hover:bg-accent/90 transition-all duration-300 shadow-lg shadow-accent/30 hover:shadow-xl hover:shadow-accent/40"
          >
            Load More Projects
          </button>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!filteredProjects.length" class="text-center py-16">
        <p class="text-gray-500 dark:text-gray-400 text-lg">
          No projects found for this filter.
        </p>
      </div>
    </div>

    <!-- Case Study Modal -->
    <CaseStudyModal
      :is-open="isModalOpen"
      :project="selectedProject"
      @close="closeModal"
    />
  </section>
</template>

<script setup>
import { ref, computed } from 'vue';
import CaseStudyCard from '@/components/ui/CaseStudyCard.vue';
import CaseStudyModal from '@/components/ui/CaseStudyModal.vue';
import { portfolio } from '@/data/portfolio.js';

const isModalOpen = ref(false);
const selectedProject = ref(null);
const activeFilter = ref('All');
const visibleCount = ref(6);
const PROJECTS_PER_PAGE = 6;

const filters = ['All', 'Web Design', 'E-Commerce', 'Platforms'];

const filteredProjects = computed(() => {
  if (activeFilter.value === 'All') {
    return portfolio;
  }
  return portfolio.filter(p => p.service === activeFilter.value);
});

const visibleProjects = computed(() => {
  return filteredProjects.value.slice(0, visibleCount.value);
});

const changeFilter = (filter) => {
  if (filter === activeFilter.value) return;
  activeFilter.value = filter;
  visibleCount.value = PROJECTS_PER_PAGE;
};

const loadMore = () => {
  visibleCount.value += PROJECTS_PER_PAGE;
};

const openModal = (project) => {
  selectedProject.value = project;
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  setTimeout(() => {
    selectedProject.value = null;
  }, 300);
};
</script>
